import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building, Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { companyInfo, contactInfo, products } from "../mock";
import { getCMSContentWithDefaults } from "../utils/cmsHelper";

const Footer = () => {
  const navigate = useNavigate();
  const [cmsContact, setCmsContact] = useState({});
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: ''
  });

  useEffect(() => {
    const loadContent = async () => {
      // Load contact info from database - database is the source of truth
      const content = await getCMSContentWithDefaults('contact', {
        // UI placeholders only for empty fields
        addresses: contactInfo.addresses,
        general_email: contactInfo.generalEmail
      });
      setCmsContact(content);
      
      // Load social links from home CMS section - database is the source of truth
      const homeContent = await getCMSContentWithDefaults('home', {
        social_facebook: '',
        social_instagram: '',
        social_linkedin: '',
        social_twitter: ''
      });
      setSocialLinks({
        facebook: homeContent.social_facebook || '',
        instagram: homeContent.social_instagram || '',
        linkedin: homeContent.social_linkedin || '',
        twitter: homeContent.social_twitter || ''
      });
    };
    loadContent();
  }, []);

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Show only first 2 addresses
  const displayAddresses = (cmsContact.addresses || contactInfo.addresses).slice(0, 2);
  
  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.jpg" 
                alt="G.A Technology & Power City Logo" 
                className="h-12 w-12 object-contain rounded-lg bg-white p-1"
              />
              <div>
                <span className="text-lg font-bold">{companyInfo.name.split('&')[0]}</span>
                <span className="text-sm text-gray-300 block">& Power City</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex space-x-3">
              {socialLinks.facebook && (
                <a 
                  href={socialLinks.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {socialLinks.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-pink-400 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialLinks.linkedin && (
                <a 
                  href={socialLinks.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-400 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {socialLinks.twitter && (
                <a 
                  href={socialLinks.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-sky-400 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {/* Show placeholder icons if no social links are set */}
              {!socialLinks.facebook && !socialLinks.instagram && !socialLinks.linkedin && !socialLinks.twitter && (
                <>
                  <span className="text-gray-600" title="Add Facebook link in Admin CMS">
                    <Facebook className="h-5 w-5" />
                  </span>
                  <span className="text-gray-600" title="Add Instagram link in Admin CMS">
                    <Instagram className="h-5 w-5" />
                  </span>
                  <span className="text-gray-600" title="Add LinkedIn link in Admin CMS">
                    <Linkedin className="h-5 w-5" />
                  </span>
                  <span className="text-gray-600" title="Add Twitter link in Admin CMS">
                    <Twitter className="h-5 w-5" />
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button onClick={() => handleLinkClick("/")} className="text-gray-300 hover:text-white transition-colors text-sm text-left">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => handleLinkClick("/about")} className="text-gray-300 hover:text-white transition-colors text-sm text-left">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => handleLinkClick("/projects")} className="text-gray-300 hover:text-white transition-colors text-sm text-left">
                  Projects
                </button>
              </li>
              <li>
                <button onClick={() => handleLinkClick("/certificates")} className="text-gray-300 hover:text-white transition-colors text-sm text-left">
                  Certificates
                </button>
              </li>
              <li>
                <button onClick={() => handleLinkClick("/contact")} className="text-gray-300 hover:text-white transition-colors text-sm text-left">
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Our Services</h3>
            <ul className="space-y-2">
              {products.slice(0, 5).map((product) => (
                <li key={product.id}>
                  <button
                    onClick={() => handleLinkClick(`/products/${product.id}`)}
                    className="text-gray-300 hover:text-white transition-colors text-sm text-left"
                  >
                    {product.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Info</h3>
            <div className="space-y-3">
              {displayAddresses.map((address, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-blue-400 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">{address.title || `${address.city} Office`}</div>
                      <div className="text-gray-300 text-sm">{address.address}</div>
                    </div>
                  </div>
                  {address.phone && (
                    <div className="flex items-center gap-2 ml-6">
                      <Phone className="h-4 w-4 text-blue-400" />
                      <span className="text-gray-300 text-sm">{address.phone}</span>
                    </div>
                  )}
                </div>
              ))}
              
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-400" />
                <a
                  href={`mailto:${cmsContact.general_email || contactInfo.generalEmail}`}
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  {cmsContact.general_email || contactInfo.generalEmail}
                </a>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-400" />
                <span className="text-gray-300 text-sm">24/7 Emergency Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-300 text-sm">
              © 2024 {companyInfo.name}. All rights reserved.
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;